import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ResponseDialogComponent } from '../response-dialog/response-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { PuzzleService } from 'src/app/services/puzzle.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ResponseSmallDialogComponent } from '../response-small-dialog/response-small-dialog.component';
import { Candidate } from 'src/app/interfaces/Candidate';
import { constants } from 'src/app/services/constants';
import { CandidateDocument } from 'src/app/interfaces/CandidateDocument';
import { DocumentationTypeInfo } from 'src/app/interfaces/DocumentationTypeInfo';
import { MultiPhotoDialogComponent } from '../multi-photo-dialog/multi-photo-dialog.component';

@Component({
  selector: 'app-upload-cv',
  templateUrl: './upload-cv.component.html',
  styleUrls: ['./upload-cv.component.css'],
})
export class UploadCvComponent implements OnInit {
  selectedFile: File | null = null;
  sending = false;
  candidate: Candidate = null;
  fileName: string = 'CV';
  innerDocs: DocumentationTypeInfo[];

  constructor(
    public dialog: MatDialog,
    private translate: TranslateService,
    private puzzleService: PuzzleService,
    private dialogRef: MatDialogRef<UploadCvComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.candidate = data.candidate;
    if (data.fileName) {
      this.fileName = data.fileName;
    }
  }

  ngOnInit(): void {
    this.getInnerDocs();
  }

  removeFile() {
    this.selectedFile = null;
  }

  // Maneja el evento de selección de archivo
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      this.validateFile();
    }
    // resetear el input
    input.value = '';
  }

  // Maneja el evento de arrastrar y soltar archivos
  onFileDropped(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.selectedFile = files[0]; // Solo permite un archivo
      this.validateFile();
    }
    const dropzone = event.currentTarget as HTMLElement;
    dropzone.classList.remove('dragover');
  }

  validateFile() {
    if (this.selectedFile) {
      const allowedExtensions = constants.allowedExt;
      const allowedSize = constants.maxFileSize;
      const extension = this.selectedFile.name.split('.').pop().toLowerCase();

      // si el nombre del archivo tiene mas de 100 caracteres, mostrar error
      if (this.selectedFile.name.length > 100) {
        let title = this.translate.instant('extErrorFileNameLength');
        let message =
          '<br>' +
          this.selectedFile.name +
          '<br><br>' +
          this.translate.instant('extErrorFileNameLengthMessage');
        this.openResultDialog(0, title, message);
        this.selectedFile = null;
        return;
      }

      if (!allowedExtensions.includes(`${extension?.toLowerCase()}`)) {
        let title = this.translate.instant('extErrorNotPermitted');
        let message =
          '<br>' +
          this.selectedFile.name +
          '<br><br>' +
          this.translate.instant('extErrorNotPermittedTypes');
        this.openResultDialog(0, title, message);
        this.selectedFile = null;
        return;
      }

      if (this.selectedFile.size > allowedSize) {
        let title = this.translate.instant('extErrorFileSize');
        let message =
          '<br>' +
          this.selectedFile.name +
          '<br><br>' +
          this.translate.instant('extErrorFileSizeMessage');
        this.openResultDialog(0, title, message);
        this.selectedFile = null;
        return;
      }

      // revisar si no esta vacio el archivo
      if (this.selectedFile.size == 0) {
        let title = this.translate.instant('extErrorEmptyFile');
        let message =
          '<br>' +
          this.selectedFile.name +
          '<br><br>' +
          this.translate.instant('extErrorEmptyFileMessage');
        this.openResultDialog(0, title, message);
        this.selectedFile = null;
        return;
      }

      // si inicia con ~$ y pesa menos de 500 bytes, es un archivo temporal de office
      if (
        /* this.selectedFile.name.startsWith('~$') && */
        this.selectedFile.size < 500
      ) {
        let title = this.translate.instant('extErrorTempFile');
        let message =
          '<br>' +
          this.selectedFile.name +
          '<br><br>' +
          this.translate.instant('extErrorTempFileMessage');
        this.selectedFile = null;
        this.openResultDialog(0, title, message);
        return;
      }

      // si es word abrir dialogo de confirmacion
      if (extension == 'doc' || extension == 'docx') {
        let dialog = this.openConfirmDialog();
      }

      // revisar si es imagen
      const imageExt = ['jpg', 'jpeg', 'gif', 'png', 'webp'];
      let isImage = imageExt.includes(extension);
      if (isImage) {
        let dialog = this.dialog.open(MultiPhotoDialogComponent, {
          width: '600px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          panelClass: 'compact-dialog',
          data: {
            name: this.fileName,
            file: this.selectedFile,
          },
        });

        dialog.afterClosed().subscribe((result: File) => {
          if (result) {
            this.selectedFile = result;

            // si inicia con ~$ y pesa menos de 500 bytes, es un archivo temporal de office
            if (/* this.selectedFile.name.startsWith('~$') && */ this.selectedFile.size < 500) {
              let title = this.translate.instant('extErrorTempFile');
              let message =
                '<br>' +
                this.selectedFile.name +
                '<br><br>' +
                this.translate.instant('extErrorTempFileMessage');
              this.openResultDialog(0, title, message);
              this.selectedFile = null;
              return;
            }

            let fileSize = this.selectedFile.size;
            let maxFileSize = constants.maxFileSize;
            if (fileSize > maxFileSize) {
              let title = this.translate.instant('extErrorFileSize');
              let message =
                '<br>' +
                this.selectedFile.name +
                '<br><br>' +
                this.translate.instant('extErrorFileSizeMessage');
              this.openResultDialog(0, title, message);
              this.selectedFile = null;
              return;
            }

            // revisar si no esta vacio el archivo
            if (fileSize == 0) {
              let title = this.translate.instant('extErrorEmptyFile');
              let message =
                '<br>' +
                this.selectedFile.name +
                '<br><br>' +
                this.translate.instant('extErrorEmptyFileMessage');
              this.openResultDialog(0, title, message);
              this.selectedFile = null;
              return;
            }
          }
        });
        return;
      }
    }
  }

  openResultDialog(mode: number, message?: string, content?: string) {
    let data: string[] = [];
    switch (mode) {
      case 0:
        {
          data.push('Error');
          data.push(message);
          if (content) data.push(content);
          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
          });
        }
        break;
      case 1:
        {
          data.push('Exito');
          data.push(message);
          if (content) data.push(content);

          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
          });
        }
        break;
      case 2:
        {
          data.push('loading');
          data.push(message);
          if (content) data.push(content);

          return this.dialog.open(ResponseSmallDialogComponent, {
            width: '500px',
            data: data,
            disableClose: true,
          });
        }
        break;
    }
  }

  openConfirmDialog() {
    let data = [];
    const title = this.translate.instant('wordFileToPDF');
    const confirmMessage = this.translate.instant('wordFileToPDFMessage');

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: [title, confirmMessage],
    });

    return dialogRef;
  }

  // Evita el comportamiento por defecto en el dragover
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Añadir clase cuando el archivo esté sobre el área
    const dropzone = event.currentTarget as HTMLElement;
    dropzone.classList.add('dragover');
  }

  // Maneja el evento dragleave (opcional)
  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    // Remover clase cuando el archivo ya no esté sobre el área
    const dropzone = event.currentTarget as HTMLElement;
    dropzone.classList.remove('dragover');
  }

  getInnerDocs() {
    this.puzzleService
      .getInnerDocuments(this.candidate.cdT_COMPANY_ID, this.candidate.cdT_ID)
      .subscribe((response) => {
        this.innerDocs = response;
      });
  }

  // Simula el proceso de subida de archivo
  uploadFile(): void {
    if (!this.selectedFile || this.sending) return;

    this.sending = true;
    const loadingMessage = this.translate.instant(
      'candidateDetailAlertLoading',
    );
    const loading = this.openResultDialog(2, loadingMessage);

    const doc = this.visibleInnerDocs.find(
      (doc) => doc.pdT_NAME === this.fileName,
    );

    const formData = new FormData();
    formData.append('candidateId', this.candidate.cdT_ID);
    formData.append('companyId', this.candidate.cdT_COMPANY_ID);
    formData.append('fileWrapper.file', this.selectedFile);
    formData.append('cdOC_DOCUMENT_TYPE_ID', constants.candidateDocumentId);
    formData.append('cdOC_PERSONAL_DOCUMENTATION_TYPE_ID', doc.pdT_ID);

    this.puzzleService.sendDocs(formData).subscribe(
      (response) => {
        this.sending = false;
        loading.close();
        if (response.isSuccess) {
          this.openResponseDialog(
            'Exito',
            this.translate.instant(response.message),
          );
          this.dialogRef.close(true);
        } else {
          this.openResponseDialog(
            'Error',
            this.translate.instant('uploadCVDialogError'),
          );
        }
      },
      (error) => {
        this.sending = false;
        loading.close();
        this.openResponseDialog(
          'Error',
          this.translate.instant('uploadCVDialogError'),
        );
      },
    );
  }

  get visibleInnerDocs() {
    if (!this.innerDocs) return [];
    let list = this.innerDocs.filter((doc) => doc.pdT_VISIBLE !== false);
    return list;
  }

  openResponseDialog(response: string, message?: string, name?: string) {
    let data: string[] = [];
    if (response == 'Error') {
      data.push('Error');
      data.push(message);
      if (name) data.push(name);
      const dialogRef = this.dialog.open(ResponseDialogComponent, {
        width: '500px',
        /* height: '400px', */
        data: data,
      });
    } else if (response == 'Exito') {
      data.push('Exito');
      data.push(message);

      const dialogRef = this.dialog.open(ResponseDialogComponent, {
        width: '500px',
        /* height: '400px', */
        data: data,
      });
    }
  }
}
